import { useCallback } from "react";
import { isEqual } from "lodash";
import { notifyError } from "@linkfluence/core/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { formatCSV } from "@radarly/models/csv";
import { SearchFragment } from "@radarly/models/search";

export const useGenerateCSV = <T extends SearchFragment>(
  title: string,
  fragment: T,
  evolutionFragment?: T
) => {
  const queryClient = useQueryClient();
  const locale = useCurrentUserLocale();
  const facets = useFacetsFromContext();

  const getFragmentWithData = useCallback(
    (fragment: T): T => {
      let fragmentWithData: T;
      queryClient
        .getQueriesData<T["data"]>(["/search/v3"])
        .find(([key, data]) => {
          const isMatch = key.some((k) => isEqual(k, fragment.request));
          if (isMatch) {
            fragmentWithData = { ...fragment, data };
            return true;
          }
          return false;
        });
      return fragmentWithData;
    },
    [queryClient]
  );

  const downloadCSV = useCallback(
    (content: BlobPart) => {
      const csvData = new Blob([content], { type: "text/csv" });
      const csvURL = URL.createObjectURL(csvData);
      const link = document.createElement("a");
      link.href = csvURL;
      link.download = `${title}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [title]
  );

  const onGenerateCSV = useCallback(async () => {
    try {
      let fragmentWithData: T;
      let evolutionFragmentWithData: T;
      if (fragment) {
        fragmentWithData = getFragmentWithData(fragment);
      }
      if (evolutionFragment) {
        evolutionFragmentWithData = getFragmentWithData({
          ...evolutionFragment,
          request: {
            ...evolutionFragment.request,
            id: evolutionFragment.request.id,
          },
        });
      }
      const content = formatCSV(
        locale,
        facets,
        fragmentWithData,
        evolutionFragmentWithData
      );
      return downloadCSV(content);
    } catch (err) {
      return notifyError(tr(locale, "INSIGHT_PAGES_CSV_EXPORT_ERROR"));
    }
  }, [
    downloadCSV,
    evolutionFragment,
    facets,
    fragment,
    getFragmentWithData,
    locale,
  ]);

  return {
    onGenerateCSV,
  };
};
